import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  FaCreditCard,
  FaMoneyBillWave,
  FaMapMarkerAlt,
  FaUniversity,
  FaCheckCircle,
  FaArrowLeft,
  FaShieldAlt,
  FaShoppingBag,
  FaTag,
} from "react-icons/fa";

function CheckoutPage() {
  const navigate = useNavigate();
  const parentId = localStorage.getItem("parentId");
  const [cart, setCart] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState("COD");
  const [isProcessing, setIsProcessing] = useState(false);

  const BASE_URL =
    process.env.REACT_APP_BACKEND_URL || "https://uniformnx-com.onrender.com";

  console.log("BASE_URL:", BASE_URL);

  const [address, setAddress] = useState({
    name: "",
    phone: "",
    addressLine: "",
    city: "",
    pincode: "",
  });

  useEffect(() => {
    loadCart();
    fetchParentData();
  }, []);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const existingScript = document.getElementById("razorpay-script");
      if (existingScript) {
        resolve(true);
        return;
      }

      const script = document.createElement("script");
      script.id = "razorpay-script";
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const validateCheckout = () => {
    const requiredFields = ["name", "phone", "addressLine", "city", "pincode"];

    for (const field of requiredFields) {
      if (!address[field] || address[field].trim() === "") {
        Swal.fire("Error", `Please fill ${field}`, "error");
        return false;
      }
    }

    if (address.phone.length !== 10) {
      Swal.fire("Error", "Please enter valid 10-digit phone number", "error");
      return false;
    }

    if (address.pincode.length !== 6) {
      Swal.fire("Error", "Please enter valid 6-digit pincode", "error");
      return false;
    }

    if (cart.length === 0) {
      Swal.fire("Error", "Cart is empty", "error");
      return false;
    }

    return true;
  };

  const fetchParentData = async () => {
    if (!parentId) return;

    try {
      const res = await axios.get(`${BASE_URL}/orders/parent-profile/${parentId}`);
      if (res.data && res.data.address) {
        setAddress(res.data.address);
      }
    } catch (err) {
      console.log("No saved address found.");
    }
  };

  const loadCart = async () => {
    if (!parentId) return;

    try {
      const res = await axios.get(`${BASE_URL}/cart/${parentId}`);

      console.log("CART API RESPONSE:", res.data);

      const normalizedCart = res.data.map((item) => ({
        uniformId: item.uniformId || item.uniform || null,
        quantity: item.quantity || 1,
        size: item.size || "",
        childId: item.childId || null,
        fallbackName:
          item.name || item.uniformId?.name || item.uniform?.name || "",
        fallbackPrice:
          item.price || item.uniformId?.price || item.uniform?.price || 0,
      }));

      setCart(normalizedCart);
    } catch (err) {
      console.log("Cart load failed", err);
      setCart([]);
    }
  };

  const totalAmount = cart.reduce((acc, i) => {
    const price = i.uniformId?.price || i.fallbackPrice || 0;
    return acc + price * i.quantity;
  }, 0);

  const placeOrder = async ({
    paymentMethodValue = paymentMethod,
    paymentStatus = "Pending",
    razorpayOrderId = "",
    razorpayPaymentId = "",
  } = {}) => {
    try {
      await axios.post(`${BASE_URL}/orders/order`, {
        parentId,
        items: cart,
        deliveryType: "Home Delivery",
        paymentMethod: paymentMethodValue,
        paymentStatus,
        address,
        razorpayOrderId,
        razorpayPaymentId,
      });

      await axios.put(`${BASE_URL}/cart/afterOrder/${parentId}`);
      window.dispatchEvent(new Event("cartUpdate"));

      Swal.fire({
        icon: "success",
        title: "Order Placed 🎉",
        text: "Redirecting...",
      }).then(() => {
        navigate("/parent/orders");
      });
    } catch (err) {
      console.error("Order Failed:", err);
      Swal.fire("Error", "Order Failed", "error");
    }
  };

  const handleCODOrder = async () => {
    if (!validateCheckout()) return;
    setIsProcessing(true);
    await placeOrder({
      paymentMethodValue: "COD",
      paymentStatus: "Pending",
    });
    setIsProcessing(false);
  };

  const handleRazorpayPayment = async () => {
    if (!validateCheckout()) return;

    setIsProcessing(true);

    const scriptLoaded = await loadRazorpayScript();
    if (!scriptLoaded) {
      setIsProcessing(false);
      Swal.fire("Error", "Razorpay SDK could not be loaded", "error");
      return;
    }

    try {
      const orderResponse = await axios.post(`${BASE_URL}/api/payment/create-order`, {
        amount: totalAmount,
      });

      const { key, order } = orderResponse.data;

      const options = {
        key,
        amount: order.amount,
        currency: order.currency,
        name: "UniformNX",
        description: "School Uniform Order Payment",
        order_id: order.id,
        handler: async function (response) {
          try {
            const verifyResponse = await axios.post(
              `${BASE_URL}/api/payment/verify-payment`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              }
            );

            if (verifyResponse.data.success) {
              await placeOrder({
                paymentMethodValue: "RAZORPAY",
                paymentStatus: "Paid",
                razorpayOrderId: response.razorpay_order_id,
                razorpayPaymentId: response.razorpay_payment_id,
              });
            } else {
              Swal.fire("Error", "Payment verification failed", "error");
            }
          } catch (error) {
            Swal.fire("Error", "Verification failed", "error");
          } finally {
            setIsProcessing(false);
          }
        },
        prefill: {
          name: address.name || "Customer",
          contact: address.phone || "",
        },
        notes: {
          parentId: parentId || "",
          city: address.city || "",
        },
        theme: {
          color: "#097969",
        },
        modal: {
          ondismiss: function () {
            setIsProcessing(false);
          },
        },
      };

      const rzp = new window.Razorpay(options);

      rzp.on("payment.failed", function () {
        setIsProcessing(false);
        Swal.fire("Error", "Payment failed or cancelled", "error");
      });

      rzp.open();
    } catch (error) {
      console.error("Payment Error:", error);
      setIsProcessing(false);
      Swal.fire("Error", "Payment could not be started", "error");
    }
  };

  return (
    <div className="checkout-wrapper">
      <div className="container py-5">
        <div className="checkout-header mb-5">
          <button className="btn btn-outline" onClick={() => navigate("/parent/cart")}>
            <FaArrowLeft /> Back to Cart
          </button>
          <h2 className="mt-3 fw-bold">Secure Checkout</h2>
          <p className="text-muted">Review your order and select payment method</p>
        </div>

        <div className="row g-4">
          <div className="col-lg-8">
            <div className="checkout-card mb-4">
              <div className="card-head">
                <FaMapMarkerAlt className="icon-main" />
                <div>
                  <h5>Delivery Address</h5>
                  <p className="card-subtitle">All fields are required for delivery</p>
                </div>
              </div>

              <div className="row g-3 p-4">
                <div className="col-md-6">
                  <label className="form-label-custom required">Full Name *</label>
                  <input
                    className="form-input-p"
                    value={address.name}
                    onChange={(e) => setAddress({ ...address, name: e.target.value })}
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label-custom required">Phone Number *</label>
                  <input
                    className="form-input-p"
                    maxLength="10"
                    value={address.phone}
                    onChange={(e) =>
                      setAddress({
                        ...address,
                        phone: e.target.value.replace(/\D/g, ""),
                      })
                    }
                    placeholder="10-digit mobile number"
                  />
                </div>

                <div className="col-12">
                  <label className="form-label-custom required">Address Line *</label>
                  <textarea
                    className="form-input-p"
                    rows="3"
                    value={address.addressLine}
                    onChange={(e) =>
                      setAddress({ ...address, addressLine: e.target.value })
                    }
                    placeholder="Complete delivery address"
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label-custom required">City *</label>
                  <input
                    className="form-input-p"
                    value={address.city}
                    onChange={(e) => setAddress({ ...address, city: e.target.value })}
                    placeholder="Your city"
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label-custom required">Pincode *</label>
                  <input
                    className="form-input-p"
                    maxLength="6"
                    value={address.pincode}
                    onChange={(e) =>
                      setAddress({
                        ...address,
                        pincode: e.target.value.replace(/\D/g, ""),
                      })
                    }
                    placeholder="6-digit pincode"
                  />
                </div>
              </div>
            </div>

            <div className="checkout-card mb-4">
              <div className="card-head">
                <FaCreditCard className="icon-main" />
                <h5>Payment Method</h5>
              </div>

              <div className="p-4">
                <div className="payment-grid">
                  <div
                    className={`pay-option ${paymentMethod === "COD" ? "active" : ""}`}
                    onClick={() => setPaymentMethod("COD")}
                  >
                    <FaMoneyBillWave className="pay-icon" />
                    <div className="pay-info">
                      <h6>Cash on Delivery</h6>
                      <span>Pay when you receive items</span>
                    </div>
                    {paymentMethod === "COD" && (
                      <FaCheckCircle className="check-mark" />
                    )}
                  </div>

                  <div
                    className={`pay-option ${
                      paymentMethod === "RAZORPAY" ? "active" : ""
                    }`}
                    onClick={() => setPaymentMethod("RAZORPAY")}
                  >
                    <FaUniversity className="pay-icon" />
                    <div className="pay-info">
                      <h6>Online Payment</h6>
                      <span>Pay securely with Razorpay</span>
                    </div>
                    {paymentMethod === "RAZORPAY" && (
                      <FaCheckCircle className="check-mark" />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="order-summary-enhanced">
              <div className="summary-header">
                <div className="summary-icon-wrapper">
                  <FaShoppingBag className="summary-main-icon" />
                </div>
                <div>
                  <h5 className="summary-title">Order Summary</h5>
                  <p className="summary-subtitle">Review your items before checkout</p>
                </div>
              </div>

              <div className="order-items-container">
                <div className="items-scroll-wrapper">
                  {cart.map((i, index) => (
                    <div key={index} className="order-item-modern">
                      <div className="item-info">
                        <div className="item-icon">
                          <FaTag className="item-tag-icon" />
                        </div>
                        <div className="item-details">
                          <h6 className="item-name">
                            {i.uniformId?.name || i.fallbackName || "Uniform Item"}
                          </h6>
                          <span className="item-quantity">Quantity: {i.quantity}</span>
                        </div>
                      </div>
                      <div className="item-price-wrapper">
                        <span className="item-price">
                          ₹{(
                            (i.uniformId?.price || i.fallbackPrice || 0) * i.quantity
                          ).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="summary-divider"></div>

              <div className="total-section">
                <div className="total-row">
                  <div className="total-info">
                    <span className="total-label">Payable Amount</span>
                    <span className="total-description">Including all taxes</span>
                  </div>
                  <div className="total-amount-wrapper">
                    <span className="total-amount">₹{totalAmount.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <button
                className={`place-order-btn ${
                  paymentMethod === "COD" ? "cod-style" : "bank-style"
                }`}
                onClick={
                  paymentMethod === "COD" ? handleCODOrder : handleRazorpayPayment
                }
                disabled={isProcessing}
              >
                <div className="button-content">
                  {paymentMethod === "COD" ? (
                    <>
                      <FaMoneyBillWave className="btn-icon" />
                      <span>{isProcessing ? "Processing..." : "Place COD Order"}</span>
                    </>
                  ) : (
                    <>
                      <FaUniversity className="btn-icon" />
                      <span>{isProcessing ? "Processing..." : "Pay with Razorpay"}</span>
                    </>
                  )}
                </div>
              </button>

              <div className="security-badge">
                <FaShieldAlt className="security-icon" />
                <span>Secure & Safe Payment</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

        .checkout-wrapper {
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          min-height: 100vh;
          font-family: 'Inter', sans-serif;
        }

        .checkout-card {
          background: white;
          border-radius: 24px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0,0,0,0.08);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .checkout-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 15px 40px rgba(0,0,0,0.12);
        }

        .card-head {
          padding: 24px 28px;
          border-bottom: 1px solid #f1f5f9;
          display: flex;
          align-items: center;
          gap: 16px;
          background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
        }

        .card-head h5 {
          margin: 0;
          font-weight: 700;
          color: #1e293b;
          font-size: 1.2rem;
        }

        .card-subtitle {
          margin: 4px 0 0 0;
          color: #64748b;
          font-size: 0.8rem;
          font-weight: 500;
        }

        .icon-main {
          color: #3b82f6;
          font-size: 1.4rem;
          background: rgba(59, 130, 246, 0.1);
          padding: 8px;
          border-radius: 12px;
        }

        .form-label-custom {
          font-size: 0.875rem;
          font-weight: 600;
          color: #475569;
          margin-bottom: 8px;
          display: block;
        }

        .form-label-custom.required {
          color: #1e293b;
          font-weight: 700;
        }

        .form-input-p {
          width: 100%;
          padding: 14px 18px;
          border-radius: 16px;
          border: 2px solid #e2e8f0;
          outline: none;
          transition: all 0.3s ease;
          font-size: 0.95rem;
          background: #ffffff;
        }

        .form-input-p:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          transform: translateY(-1px);
        }

        .payment-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }

        .pay-option {
          border: 2px solid #f1f5f9;
          border-radius: 20px;
          padding: 24px;
          cursor: pointer;
          position: relative;
          transition: all 0.3s ease;
          display: flex;
          align-items: flex-start;
          gap: 16px;
          background: #ffffff;
        }

        .pay-option:hover {
          border-color: #cbd5e1;
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.08);
        }

        .pay-option.active {
          border-color: #10b981;
          background: linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%);
          box-shadow: 0 8px 25px rgba(16, 185, 129, 0.15);
        }

        .pay-icon {
          font-size: 1.25rem;
          color: #097969;
          margin-top: 2px;
        }

        .pay-info h6 {
          margin: 0 0 4px 0;
          font-weight: 700;
          color: #1e293b;
        }

        .pay-info span {
          color: #64748b;
          font-size: 0.9rem;
        }

        .check-mark {
          margin-left: auto;
          color: #10b981;
          font-size: 1.1rem;
        }

        .order-summary-enhanced {
          background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
          border-radius: 28px;
          padding: 32px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 20px 40px rgba(0,0,0,0.06);
          position: sticky;
          top: 100px;
        }

        .summary-header {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 28px;
          padding-bottom: 20px;
          border-bottom: 2px solid #f1f5f9;
        }

        .summary-icon-wrapper {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          width: 56px;
          height: 56px;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 8px 20px rgba(16, 185, 129, 0.25);
        }

        .summary-main-icon {
          color: white;
          font-size: 1.6rem;
        }

        .summary-title {
          font-weight: 800;
          color: #1e293b;
          font-size: 1.4rem;
          margin: 0;
          line-height: 1.2;
        }

        .summary-subtitle {
          color: #64748b;
          font-size: 0.875rem;
          margin: 4px 0 0 0;
          font-weight: 500;
        }

        .order-items-container {
          margin-bottom: 24px;
        }

        .items-scroll-wrapper {
          max-height: 280px;
          overflow-y: auto;
          padding-right: 8px;
        }

        .items-scroll-wrapper::-webkit-scrollbar {
          width: 6px;
        }

        .items-scroll-wrapper::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }

        .items-scroll-wrapper::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }

        .order-item-modern {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px;
          margin-bottom: 12px;
          background: #ffffff;
          border-radius: 16px;
          border: 1px solid #f1f5f9;
          transition: all 0.3s ease;
        }

        .order-item-modern:hover {
          border-color: #e2e8f0;
          transform: translateX(4px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.04);
        }

        .item-info {
          display: flex;
          align-items: center;
          gap: 12px;
          flex: 1;
        }

        .item-icon {
          background: #f8fafc;
          width: 40px;
          height: 40px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .item-tag-icon {
          color: #3b82f6;
          font-size: 0.9rem;
        }

        .item-details {
          flex: 1;
        }

        .item-name {
          font-weight: 600;
          color: #1e293b;
          font-size: 0.95rem;
          margin: 0;
          line-height: 1.3;
        }

        .item-quantity {
          color: #64748b;
          font-size: 0.8rem;
          font-weight: 500;
        }

        .item-price-wrapper {
          text-align: right;
        }

        .item-price {
          font-weight: 700;
          color: #1e293b;
          font-size: 1rem;
        }

        .summary-divider {
          height: 2px;
          background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 100%);
          margin: 24px 0;
          border-radius: 2px;
        }

        .total-section {
          margin-bottom: 28px;
        }

        .total-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
          border-radius: 20px;
          border: 2px solid #bbf7d0;
        }

        .total-info {
          flex: 1;
        }

        .total-label {
          display: block;
          font-weight: 800;
          color: #1e293b;
          font-size: 1.1rem;
          line-height: 1.2;
        }

        .total-description {
          color: #64748b;
          font-size: 0.8rem;
          font-weight: 500;
          margin-top: 2px;
        }

        .total-amount-wrapper {
          text-align: right;
        }

        .total-amount {
          font-weight: 800;
          color: #10b981;
          font-size: 1.5rem;
          display: block;
        }

        .place-order-btn {
          width: 100%;
          padding: 18px;
          border-radius: 20px;
          border: none;
          font-weight: 700;
          font-size: 1.1rem;
          transition: all 0.3s ease;
          cursor: pointer;
          position: relative;
          overflow: hidden;
          margin-bottom: 20px;
        }

        .place-order-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .place-order-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
          transition: left 0.5s ease;
        }

        .place-order-btn:hover::before {
          left: 100%;
        }

        .cod-style {
          background: #097969;
          color: white;
          box-shadow: 0 10px 25px rgba(9, 121, 105, 0.3);
        }

        .cod-style:hover {
          transform: translateY(-2px);
          box-shadow: 0 15px 35px rgba(9, 121, 105, 0.4);
        }

        .bank-style {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
          box-shadow: 0 10px 25px rgba(59, 130, 246, 0.3);
        }

        .bank-style:hover {
          transform: translateY(-2px);
          box-shadow: 0 15px 35px rgba(59, 130, 246, 0.4);
        }

        .button-content {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
        }

        .btn-icon {
          font-size: 1.1rem;
        }

        .security-badge {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          color: #64748b;
          font-size: 0.8rem;
          font-weight: 500;
        }

        .security-icon {
          color: #10b981;
          font-size: 0.9rem;
        }

        .swal2-popup {
          border-radius: 20px !important;
          font-family: 'Inter', sans-serif !important;
        }

        .swal2-title {
          font-weight: 800 !important;
        }

        .swal2-confirm {
          border-radius: 12px !important;
        }
      `}</style>
    </div>
  );
}

export default CheckoutPage;
